import React, { useMemo, useState } from 'react';
import { useTranslation } from 'utils/i18n';
import { getCategoriesFromProducts, getProvidersFromProducts, getTagsFromProducts } from '../ResourceDetailModal/utils/marketplaceMetadata';
import Select from 'components/product/components/primitives/Select';
import Label from 'components/product/components/primitives/Label';
import marketplaceTranslations from './translations';

const TOP_OPTIONS_COUNT = 3; // Show top 3 options by default

/**
 * Marketplace filters component - Palantir/McKinsey style
 * Provides comprehensive filtering with search, tags, category, provider, product type, and release status
 * Context-aware: adapts to Products vs Providers view
 */
export const MarketplaceFilters = ({
  products,
  filters,
  onFiltersChange,
  viewMode = 'products', // 'products' | 'providers'
  marketplaceProviders = [] // resale/partner providers from API (merged into Provider filter when on Products tab)
}) => {
  const { language } = useTranslation();
  const t = (key, defaultValue = '') => {
    const keys = key.split('.');
    let value = marketplaceTranslations[language] || marketplaceTranslations.en;
    for (const k of keys) {
      value = value?.[k];
    }
    return value || defaultValue || key;
  };

  const providersFromProducts = getProvidersFromProducts(products);
  const providers = useMemo(() => {
    const byId = new Map();
    (providersFromProducts || []).forEach(p => { if (p && p.id) byId.set(p.id, p); });
    (marketplaceProviders || []).forEach(p => {
      if (p && p.id && !byId.has(p.id)) byId.set(p.id, { id: p.id, displayName: p.name || p.displayName || p.id });
    });
    return Array.from(byId.values()).sort((a, b) => (a.displayName || a.id || '').localeCompare(b.displayName || b.id || ''));
  }, [providersFromProducts, marketplaceProviders]);
  const categories = getCategoriesFromProducts(products);
  const allTags = getTagsFromProducts(products);
  
  // State for expanded filters
  const [expandedFilters, setExpandedFilters] = useState({
    category: false,
    provider: false,
    tags: false
  });
  
  const toggleFilter = (filterName) => {
    setExpandedFilters(prev => ({
      ...prev,
      [filterName]: !prev[filterName]
    }));
  };
  
  // Check if any filters are active (for reset button)
  const hasActiveFilters = useMemo(() => {
    return (
      (filters.searchQuery && filters.searchQuery.trim() !== '') ||
      (filters.vendorType && filters.vendorType !== 'all') ||
      (filters.categoryId && filters.categoryId !== 'all') ||
      (filters.providerId && filters.providerId !== 'all') ||
      (filters.selectedTags && filters.selectedTags.length > 0) ||
      (filters.productType && filters.productType !== 'all') ||
      (filters.releaseStatus && filters.releaseStatus !== 'all')
    );
  }, [filters]);
  
  // Get filtered products count for each category/provider (for displaying counts)
  // Simplified calculation - just count products in that category/provider
  const getCategoryProductCount = (categoryId) => {
    if (!categoryId || categoryId === 'all') return products.length;
    return products.filter(p => {
      const cat = p.marketplace?.category?.id || 'other';
      return cat === categoryId;
    }).length;
  };
  
  const getProviderProductCount = (providerId) => {
    if (!providerId || providerId === 'all') return products.length;
    return products.filter(p => {
      const prov = p.marketplace?.provider?.id || '';
      return prov === providerId;
    }).length;
  };
  
  const handleSearchChange = (e) => {
    onFiltersChange({
      ...filters,
      searchQuery: e.target.value
    });
  };
  
  const handleVendorTypeChange = (vendorType) => {
    onFiltersChange({
      ...filters,
      vendorType: vendorType || 'all'
    });
  };
  
  const handleProviderChange = (providerId) => {
    onFiltersChange({
      ...filters,
      providerId: providerId || 'all'
    });
  };
  
  const handleCategoryChange = (categoryId) => {
    onFiltersChange({
      ...filters,
      categoryId: categoryId || 'all'
    });
  };
  
  const handleTagToggle = (tag) => {
    const currentTags = filters.selectedTags || [];
    const isSelected = currentTags.includes(tag);
    
    onFiltersChange({
      ...filters,
      selectedTags: isSelected
        ? currentTags.filter(t => t !== tag)
        : [...currentTags, tag]
    });
  };
  
  const handleProductTypeChange = (productType) => {
    onFiltersChange({
      ...filters,
      productType: productType || 'all'
    });
  };
  
  const handleReleaseStatusChange = (releaseStatus) => {
    onFiltersChange({
      ...filters,
      releaseStatus: releaseStatus || 'all'
    });
  };
  
  // Reset all filters
  const handleResetFilters = () => {
    onFiltersChange({
      searchQuery: '',
      vendorType: 'all',
      providerId: 'all',
      categoryId: 'all',
      selectedTags: [],
      productType: 'all',
      releaseStatus: 'all'
    });
  };
  
  const searchIcon = (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.5" fill="none"/>
      <path d="M11 11l3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  );
  
  // Get search placeholder based on view mode
  const searchPlaceholder = viewMode === 'providers' 
    ? t('filters.searchProvidersPlaceholder', 'Search providers...')
    : t('filters.searchProductsPlaceholder', 'Search products...');

  // Build options with counts (marketplace best practice)
  // Sort by product count (descending) to show most popular first
  const providerOptionsWithCounts = providers.map(p => ({
    value: p.id,
    label: p.displayName,
    count: getProviderProductCount(p.id)
  })).sort((a, b) => b.count - a.count);
  
  const categoryOptionsWithCounts = categories.map(c => ({
    value: c.id,
    label: c.displayName,
    count: getCategoryProductCount(c.id)
  })).sort((a, b) => b.count - a.count);
  
  // Get visible options (top 3 or all if expanded)
  // Always include selected options even if not in top 3
  const getVisibleOptions = (options, selectedValue, expanded) => {
    if (expanded) {
      return options;
    }
    
    const topOptions = options.slice(0, TOP_OPTIONS_COUNT);
    const selectedOption = selectedValue && selectedValue !== 'all' 
      ? options.find(opt => opt.value === selectedValue)
      : null;
    
    // If selected option is already in top 3, return top 3
    if (!selectedOption || topOptions.some(opt => opt.value === selectedOption.value)) {
      return topOptions;
    }
    
    // Otherwise, include selected option + top 2 (to keep total at 3)
    return [selectedOption, ...topOptions.slice(0, TOP_OPTIONS_COUNT - 1)];
  };
  
  const visibleProviders = getVisibleOptions(
    providerOptionsWithCounts, 
    filters.providerId, 
    expandedFilters.provider
  );
  
  const visibleCategories = getVisibleOptions(
    categoryOptionsWithCounts, 
    filters.categoryId, 
    expandedFilters.category
  );
  
  // Initialize selectedTags early - before use
  const selectedTags = filters.selectedTags || [];
  
  // Get product count by tag
  const getTagProductCount = (tag) => {
    return products.filter(p => {
      const productTags = (p.marketplace?.tags || []).map(t => 
        typeof t === 'string' ? t.trim().toLowerCase() : ''
      );
      return productTags.includes(tag.toLowerCase());
    }).length;
  };
  
  // For tags, always show selected tags + top options
  const selectedTagObjects = allTags.filter(tag => selectedTags.includes(tag));
  const unselectedTags = allTags.filter(tag => !selectedTags.includes(tag));
  const topUnselectedTags = expandedFilters.tags 
    ? unselectedTags 
    : unselectedTags.slice(0, Math.max(0, TOP_OPTIONS_COUNT - selectedTagObjects.length));
  const visibleTags = [...selectedTagObjects, ...topUnselectedTags];
  
  return (
    <div className="marketplace-filters-palantir">
      {/* Search Bar - Prominent, styled, always first */}
      <div className="filter-section filter-section-search">
        <div className="filter-group">
          <Label className="filter-label-search">
            {viewMode === 'providers' 
              ? t('filters.searchProviders', 'Search Providers')
              : t('filters.search', 'Search Products')}
          </Label>
          <div className="search-input-wrapper-palantir">
            <div className="search-icon-container">
              {searchIcon}
            </div>
            <input
              type="text"
              className="marketplace-search-input-palantir"
              placeholder={searchPlaceholder}
              value={filters.searchQuery || ''}
              onChange={handleSearchChange}
            />
          </div>
        </div>
      </div>
      
      {/* Active Filters & Reset Button */}
      {hasActiveFilters && (
        <div className="filter-actions">
          <button 
            type="button"
            className="filter-reset-button-palantir"
            onClick={handleResetFilters}
            title={t('filters.clearAll', 'Clear all filters')}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
            {t('filters.clearAll', 'Clear all')}
          </button>
        </div>
      )}
      
      {viewMode === 'products' ? (
        <>
          {/* Tags Filter - Multi-select chips with expand/collapse */}
          {allTags.length > 0 && (
            <div className="filter-section">
              <div className="filter-group">
                <Label className="filter-label">
                  {t('filters.tags', 'Tags')}
                  {selectedTags.length > 0 && (
                    <span className="filter-active-indicator" title={t('filters.tagsSelected', '{count} tag(s) selected').replace('{count}', selectedTags.length)}></span>
                  )}
                </Label>
                <div className="tags-filter-container">
                  {visibleTags.map(tag => {
                    const isSelected = selectedTags.includes(tag);
                    const count = getTagProductCount(tag);
                    return (
                      <button
                        key={tag}
                        type="button"
                        className={`tag-filter-chip ${isSelected ? 'selected' : ''}`}
                        onClick={() => handleTagToggle(tag)}
                      >
                        <span className="tag-filter-text">{tag}</span>
                        <span className="tag-filter-count">({count})</span>
                      </button>
                    );
                  })}
                </div>
                {allTags.length > visibleTags.length && (
                  <button
                    type="button"
                    className="filter-expand-button"
                    onClick={() => toggleFilter('tags')}
                  >
                    {expandedFilters.tags ? (
                      <>
                        <span>{t('filters.showLess', 'Show less')}</span>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="18 15 12 9 6 15"></polyline>
                        </svg>
                      </>
                    ) : (
                      <>
                        <span>{t('filters.showMore', 'Show {count} more').replace('{count}', allTags.length - visibleTags.length)}</span>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="6 9 12 15 18 9"></polyline>
                        </svg>
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          )}
          
          {/* Product Type Filter */}
          <div className="filter-section">
            <div className="filter-group">
              <Label className="filter-label">
                {t('filters.productType', 'Product Type')}
                {filters.productType && filters.productType !== 'all' && (
                  <span className="filter-active-indicator"></span>
                )}
              </Label>
              <Select
                options={[
                  { value: 'all', label: t('filters.allProductTypes', 'All Types') },
                  { value: 'implementation', label: t('marketplaceCard.productType.implementation', 'Implementation product') },
                  { value: 'application', label: t('marketplaceCard.productType.application', 'Application product') },
                  { value: 'partner', label: t('marketplaceCard.productType.partner', 'Partner Product') }
                ]}
                value={filters.productType || 'all'}
                onChange={handleProductTypeChange}
                className="left-panel"
                size="medium"
              />
            </div>
          </div>
          
          {/* Category Filter - Expandable list */}
          {categoryOptionsWithCounts.length > 0 && (
            <div className="filter-section">
              <div className="filter-group">
                <Label className="filter-label">
                  {t('filters.category', 'Category')}
                  {filters.categoryId && filters.categoryId !== 'all' && (
                    <span className="filter-active-indicator"></span>
                  )}
                </Label>
                <div className="filter-options-list">
                  {/* "All Categories" option */}
                  <button
                    type="button"
                    className={`filter-option-item ${filters.categoryId === 'all' || !filters.categoryId ? 'selected' : ''}`}
                    onClick={() => handleCategoryChange('all')}
                  >
                    <span className="filter-option-label">{t('filters.allCategories', 'All Categories')}</span>
                    <span className="filter-option-count">({products.length})</span>
                  </button>
                  
                  {/* Top categories */}
                  {visibleCategories.map(category => {
                    const isSelected = filters.categoryId === category.value;
                    return (
                      <button
                        key={category.value}
                        type="button"
                        className={`filter-option-item ${isSelected ? 'selected' : ''}`}
                        onClick={() => handleCategoryChange(category.value)}
                      >
                        <span className="filter-option-label">{category.label}</span>
                        <span className="filter-option-count">({category.count})</span>
                      </button>
                    );
                  })}
                </div>
                {categoryOptionsWithCounts.length > visibleCategories.length && (
                  <button
                    type="button"
                    className="filter-expand-button"
                    onClick={() => toggleFilter('category')}
                  >
                    {expandedFilters.category ? (
                      <>
                        <span>{t('filters.showLess', 'Show less')}</span>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="18 15 12 9 6 15"></polyline>
                        </svg>
                      </>
                    ) : (
                      <>
                        <span>{t('filters.showMore', 'Show {count} more').replace('{count}', categoryOptionsWithCounts.length - visibleCategories.length)}</span>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="6 9 12 15 18 9"></polyline>
                        </svg>
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          )}
          
          {/* Provider Filter - Expandable list */}
          {providerOptionsWithCounts.length > 0 && (
            <div className="filter-section">
              <div className="filter-group">
                <Label className="filter-label">
                  {t('filters.provider', 'Provider')}
                  {filters.providerId && filters.providerId !== 'all' && (
                    <span className="filter-active-indicator"></span>
                  )}
                </Label>
                <div className="filter-options-list">
                  {/* "All Providers" option */}
                  <button
                    type="button"
                    className={`filter-option-item ${filters.providerId === 'all' || !filters.providerId ? 'selected' : ''}`}
                    onClick={() => handleProviderChange('all')}
                  >
                    <span className="filter-option-label">{t('filters.allProviders', 'All Providers')}</span>
                    <span className="filter-option-count">({products.length})</span>
                  </button>
                  
                  {/* Top providers */}
                  {visibleProviders.map(provider => {
                    const isSelected = filters.providerId === provider.value;
                    return (
                      <button
                        key={provider.value}
                        type="button"
                        className={`filter-option-item ${isSelected ? 'selected' : ''}`}
                        onClick={() => handleProviderChange(provider.value)}
                      >
                        <span className="filter-option-label">{provider.label}</span>
                        <span className="filter-option-count">({provider.count})</span>
                      </button>
                    );
                  })}
                </div>
                {providerOptionsWithCounts.length > visibleProviders.length && (
                  <button
                    type="button"
                    className="filter-expand-button"
                    onClick={() => toggleFilter('provider')}
                  >
                    {expandedFilters.provider ? (
                      <>
                        <span>{t('filters.showLess', 'Show less')}</span>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="18 15 12 9 6 15"></polyline>
                        </svg>
                      </>
                    ) : (
                      <>
                        <span>{t('filters.showMore', 'Show {count} more').replace('{count}', providerOptionsWithCounts.length - visibleProviders.length)}</span>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="6 9 12 15 18 9"></polyline>
                        </svg>
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          )}
          
          {/* Provider Type Filter */}
          <div className="filter-section">
            <div className="filter-group">
              <Label className="filter-label">
                {t('filters.providerType', 'Provider Type')}
                {filters.vendorType && filters.vendorType !== 'all' && (
                  <span className="filter-active-indicator"></span>
                )}
              </Label>
              <Select
                options={[
                  { value: 'all', label: t('filters.allProviderTypes', 'All Provider Types') },
                  { value: 'besper', label: t('filters.besperNative', 'Besper Native') },
                  { value: 'third_party', label: t('filters.thirdParty', 'Third Party') }
                ]}
                value={filters.vendorType || 'all'}
                onChange={(value) => handleVendorTypeChange(value || 'all')}
                className="left-panel"
                size="medium"
              />
            </div>
          </div>
          
          {/* Release Status Filter */}
          <div className="filter-section">
            <div className="filter-group">
              <Label className="filter-label">
                {t('filters.releaseStatus', 'Release Status')}
                {filters.releaseStatus && filters.releaseStatus !== 'all' && (
                  <span className="filter-active-indicator"></span>
                )}
              </Label>
              <Select
                options={[
                  { value: 'all', label: t('filters.allStatuses', 'All Statuses') },
                  { value: 'available', label: 'Available' },
                  { value: 'upcoming', label: 'Coming Soon' }
                ]}
                value={filters.releaseStatus || 'all'}
                onChange={handleReleaseStatusChange}
                className="left-panel"
                size="medium"
              />
            </div>
          </div>
        </>
      ) : (
        <>
          {/* Providers view filters - Different set */}
          <div className="filter-section">
            <div className="filter-group">
              <Label className="filter-label">
                {t('filters.industry', 'Industry')}
              </Label>
              <Select
                options={[{ value: 'all', label: 'All Industries' }]}
                value="all"
                className="left-panel"
                size="medium"
                disabled
              />
            </div>
          </div>
          
          <div className="filter-section">
            <div className="filter-group">
              <Label className="filter-label">
                {t('filters.partnerType', 'Partner Type')}
              </Label>
              <Select
                options={[{ value: 'all', label: 'All Partner Types' }]}
                value="all"
                className="left-panel"
                size="medium"
                disabled
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};


